import { useState, useRef, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { RiCloseLine } from "@remixicon/react"

import Dialog from "@/components/dialog/Dialog"
import BasicButton from "@/components/button/BasicButton"
import {
  usePdfTemplatesQuery,
  useDeletePdfTemplateMutation,
  useUpdatePdfTemplateMutation,
} from "@/hooks/api/usePdfTemplateQuery"
import { readPdfTemplateDetail } from "@/app/api/pdfTemplate"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { createGlobalStore } from "@/stores/global"
import type { PdfTemplate } from "@/types/pdfTemplate"

interface LoadTemplateDialogProps {
  open: boolean
  onClose: () => void
  onApply: (idfTemplate: number) => void
  /** 내장 Default 템플릿(설정한 리포트 템플릿 구성) 적용 */
  onApplyDefault?: () => void
  onDeleted?: (idfTemplate: number) => void
  currentTemplateId?: number | null
}

export default function LoadTemplateDialog({
  open,
  onClose,
  onApply,
  onApplyDefault,
  onDeleted,
  currentTemplateId,
}: LoadTemplateDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setSnackBar } = createGlobalStore()

  // 저장된 PDF 배치는 리포트 종류별로 따로 (2026-10-02 요청)
  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  const { data: templates, isLoading } = usePdfTemplatesQuery({ reportType: reportKind }, open)
  const deleteMutation = useDeletePdfTemplateMutation()
  const updateMutation = useUpdatePdfTemplateMutation()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset editing state when dialog closes
  useEffect(() => {
    if (!open) {
      setEditingId(null)
      setEditingName("")
    }
  }, [open])

  // Sort: lastUsed first, then by date
  const sortedTemplates = useMemo(() => {
    if (!templates) return []
    return [...templates].sort((a, b) => {
      if (a.lastUsed && !b.lastUsed) return -1
      if (!a.lastUsed && b.lastUsed) return 1
      return 0
    })
  }, [templates])

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingId != null && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingId])

  const handleApply = (template: PdfTemplate) => {
    onApply(template.idfTemplate)
  }

  const handleDelete = async (template: PdfTemplate) => {
    if (!window.confirm(t("confirmDelete", "Are you sure you want to delete this template?"))) {
      return
    }

    try {
      await deleteMutation.mutateAsync(template.idfTemplate)
      onDeleted?.(template.idfTemplate)
      if (editingId === template.idfTemplate) {
        setEditingId(null)
      }
      setSnackBar({
        open: true,
        message: t("messages.successDelete", "Successfully deleted"),
        severity: "success",
      })
    } catch {
      setSnackBar({
        open: true,
        message: t("messages.failDelete", "Failed to delete"),
        severity: "error",
      })
    }
  }

  const handleStartEdit = (template: PdfTemplate) => {
    setEditingId(template.idfTemplate)
    setEditingName(template.name)
  }

  const handleDoneEdit = async (template: PdfTemplate) => {
    const trimmed = editingName.trim()
    if (!trimmed || trimmed === template.name) {
      setEditingId(null)
      return
    }

    try {
      let content = template.content
      if (!content) {
        const detail = await queryClient.fetchQuery({
          queryKey: ["readPdfTemplateDetail", template.idfTemplate],
          queryFn: () => readPdfTemplateDetail(template.idfTemplate),
        })
        content = (detail as any)?.response?.content ?? (detail as any)?.content ?? ""
      }

      await updateMutation.mutateAsync({
        idfTemplate: template.idfTemplate,
        name: trimmed,
        reportType: template.reportType,
        content,
      })
      setEditingId(null)
    } catch {
      setSnackBar({
        open: true,
        message: t("messages.failSave", "Failed to save"),
        severity: "error",
      })
    }
  }

  const formatDate = (template: PdfTemplate) => {
    const date = template.dateUpdated || template.dateCreated
    return date || null
  }

  return (
    <Dialog
      open={open}
      title={t("loadTemplate", "Load Template")}
      onClose={onClose}
      maxWidth="560px"
      footer={
        <BasicButton type="default-outlined" size="md" onClick={onClose}>
          {t("button.close", "Close")}
        </BasicButton>
      }>
      <div className="LoadTemplateDialog">
        {isLoading && <p className="LoadTemplateDialog__empty">{t("loading", "Loading...")}</p>}

        {!isLoading && (
          <div className="LoadTemplateDialog__list">
            {/* 내장 Default — 설정한 리포트 템플릿의 섹션 구성 그대로. 저장·삭제·이름 변경 대상이 아니다 (#75) */}
            {onApplyDefault && (
              <div
                className={`LoadTemplateDialog__card${currentTemplateId == null ? " LoadTemplateDialog__card--current" : ""}`}>
                <div className="LoadTemplateDialog__card-info">
                  <div className="LoadTemplateDialog__card-name-row">
                    <span className="LoadTemplateDialog__card-name">
                      {t("defaultTemplate", "Default")}
                    </span>
                  </div>
                  <span className="LoadTemplateDialog__card-date">
                    {t("defaultTemplateDesc", "Same components as the selected report template")}
                  </span>
                </div>
                <div className="LoadTemplateDialog__card-actions">
                  <BasicButton type="primary-outlined" size="sm" onClick={onApplyDefault}>
                    {t("button.apply", "Apply")}
                  </BasicButton>
                </div>
              </div>
            )}
            {sortedTemplates.map((template) => {
              const isEditing = editingId === template.idfTemplate
              const isCurrent = currentTemplateId === template.idfTemplate
              const date = formatDate(template)

              return (
                <div
                  key={template.idfTemplate}
                  className={`LoadTemplateDialog__card${isEditing ? " LoadTemplateDialog__card--editing" : ""}${isCurrent && !isEditing ? " LoadTemplateDialog__card--current" : ""}`}>
                  <div className="LoadTemplateDialog__card-info">
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        className="LoadTemplateDialog__card-input"
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleDoneEdit(template)
                          if (e.key === "Escape") setEditingId(null)
                        }}
                      />
                    ) : (
                      <div className="LoadTemplateDialog__card-name-row">
                        <span
                          className="LoadTemplateDialog__card-name"
                          onClick={() => handleStartEdit(template)}>
                          {template.name}
                        </span>
                        {template.lastUsed && (
                          <span className="LoadTemplateDialog__card-badge">
                            {t("recent", "Recent")}
                          </span>
                        )}
                      </div>
                    )}
                    {date && <span className="LoadTemplateDialog__card-date">{date}</span>}
                  </div>

                  <div className="LoadTemplateDialog__card-actions">
                    {isEditing ? (
                      <BasicButton
                        type="primary-outlined"
                        size="sm"
                        onClick={() => handleDoneEdit(template)}
                        isLoading={updateMutation.isPending}>
                        {t("button.done", "Done")}
                      </BasicButton>
                    ) : (
                      <>
                        <BasicButton
                          type="primary-outlined"
                          size="sm"
                          onClick={() => handleApply(template)}>
                          {t("button.apply", "Apply")}
                        </BasicButton>
                        <button
                          className="LoadTemplateDialog__card-delete"
                          onClick={() => handleDelete(template)}>
                          <RiCloseLine size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Dialog>
  )
}
